<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import axios from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { isTrustedRedirect } from '@/functions/trustedRedirect';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';
import { backend_url } from '@/config/config';

const router = useRouter();
const authStore = useAuthStore();

const nickname = ref('');
const name = ref('');
const firstname = ref('');
const loading = ref(false);
const loadingInfo = ref(true);
const error = ref('');
const pendingToken = ref('');
const redirectUrl = ref('');

onMounted(async () => {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  pendingToken.value = params.get('pending') || '';
  redirectUrl.value = params.get('redirect') || '';

  if (!pendingToken.value) {
    router.replace('/login');
    return;
  }
  history.replaceState(null, '', window.location.pathname);

  try {
    const { data } = await axios.get(`${backend_url}/auth/google/pending-info`, {
      params: { token: pendingToken.value },
    });
    // Suggestion pré-remplie depuis Google, à confirmer ou corriger : le nom Google
    // est parfois un pseudo (ex: "Celine BM") qui empêcherait de retrouver une licence.
    name.value = data.name || '';
    firstname.value = data.firstname || '';
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Erreur lors de la récupération des informations.';
  } finally {
    loadingInfo.value = false;
  }
});

const complete = async () => {
  error.value = '';
  if (!name.value.trim() || !firstname.value.trim()) {
    error.value = 'Le nom et le prénom sont requis.';
    return;
  }
  if (!nickname.value.trim()) {
    error.value = 'Le pseudo est requis.';
    return;
  }
  loading.value = true;
  try {
    const { data } = await axios.post(`${backend_url}/auth/google/complete`, {
      nickname: nickname.value.trim(),
      name: name.value.trim(),
      firstname: firstname.value.trim(),
      google_token: pendingToken.value,
      redirect: redirectUrl.value || null,
    });

    if (data.consent_required) {
      router.push({
        path: '/partners/consent',
        query: { consent_token: data.consent_token, client_name: data.client_name },
      });
      return;
    }

    if (data.identity_token) {
      // Compte cree pour un partenaire externe (ex: DartUp) : identite restreinte
      // uniquement, jamais le token interne complet.
      const fragment = new URLSearchParams({ token: data.identity_token }).toString();
      window.location.href = `${redirectUrl.value}#${fragment}`;
      return;
    }

    const scopes: string[] = data.scopes || [];

    authStore.setAuthData({
      token: data.access_token,
      refreshToken: data.refresh_token,
      email: data.email,
      name: data.name || '',
      firstname: data.firstname || '',
      nickname: data.nickname || '',
      profile_picture: '',
      role: scopes.includes('super_admin') ? 'super_admin' : 'player',
      scopes,
      userId: data.userId,
    });

    if (redirectUrl.value && await isTrustedRedirect(redirectUrl.value)) {
      // Redirige vers le site tiers (Badarts, CMER) avec le fragment de tokens
      const fragment = new URLSearchParams({
        token: data.access_token,
        refresh: data.refresh_token,
        email: data.email,
        name: data.name || '',
        firstname: data.firstname || '',
        nickname: data.nickname || '',
        profile_picture: '',
        bio: '',
        userId: String(data.userId),
        scopes: scopes.join(','),
      }).toString();
      window.location.href = `${redirectUrl.value}#${fragment}`;
    } else {
      toast.success('Compte créé ! Bienvenue sur DartsHub.');
      router.replace('/profile');
    }
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Erreur lors de la création du compte.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="dh-page">
    <div class="dh-wrapper">

      <div class="dh-header">
        <div class="dh-header-content">
          <div class="dh-brand">
            <img :src="dartsHubLogo" alt="DartsHub" class="dh-brand-logo" />
          </div>
          <div class="dh-tricolor-bar">
            <span></span><span></span><span></span>
          </div>
          <p class="dh-subtitle">Dernière étape</p>
        </div>
      </div>

      <div class="dh-body">
        <p class="dh-info">
          Indiquez votre nom et prénom <strong>tels qu'affichés sur votre licence fédérale</strong> (actuelle ou à
          venir), pas un pseudonyme. Nous les avons suggérés depuis votre compte Google, vérifiez qu'ils sont
          exacts avant de continuer : c'est ce qui nous permet de retrouver votre licence automatiquement.
        </p>

        <form class="dh-form" @submit.prevent="complete">
          <div class="dh-fields-row">
            <div class="dh-field">
              <label>Nom</label>
              <input v-model="name" type="text" placeholder="Dupont" required class="dh-input" :disabled="loadingInfo" />
            </div>
            <div class="dh-field">
              <label>Prénom</label>
              <input v-model="firstname" type="text" placeholder="Jean" required class="dh-input" :disabled="loadingInfo" />
            </div>
          </div>

          <div class="dh-field">
            <label>Pseudo</label>
            <input
              v-model="nickname"
              type="text"
              placeholder="dartsmaster34"
              autocomplete="username"
              required
              class="dh-input"
            />
          </div>

          <div v-if="error" class="dh-msg fd-msg--error">{{ error }}</div>

          <button type="submit" class="dh-btn fd-btn-primary" :disabled="loading">
            <i v-if="loading" class="fa-solid fa-spinner fa-spin" />
            <span>{{ loading ? 'Création…' : 'Créer mon compte' }}</span>
          </button>
        </form>
      </div>

      <div class="dh-footer">
        <span>Authentification sécurisée par</span>
        <strong>&nbsp;DartsHub</strong>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.dh-page {
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: var(--bg);
}

.dh-wrapper {
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Segoe UI', sans-serif;
  background: var(--bg-card);
  color: var(--text);
  border-radius: 20px;
  overflow: hidden;
  width: 420px;
  max-width: 100%;
  box-shadow: 0 8px 40px color-mix(in srgb, var(--primary) 10%, transparent);
}

.dh-header {
  background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%);
  text-align: center;
}

.dh-header-content {
  padding: 22px 28px 16px;
}

.dh-brand {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.dh-brand-logo {
  height: 2.25rem;
  width: auto;
}

.dh-tricolor-bar {
  display: flex;
  height: 4px;
  width: 110px;
  margin: 0 auto 12px;
  border-radius: 10px;
  overflow: hidden;
  gap: 3px;
}

.dh-tricolor-bar span { flex: 1; border-radius: 10px; }
.dh-tricolor-bar span:nth-child(1) { background: var(--dh-bar-1); }
.dh-tricolor-bar span:nth-child(2) { background: var(--dh-bar-2); }
.dh-tricolor-bar span:nth-child(3) { background: var(--primary); }

.dh-subtitle {
  font-size: 0.76rem;
  color: rgba(255,255,255,0.7);
  margin: 0 0 14px;
  font-weight: 300;
}

.dh-body {
  padding: 24px 28px;
  flex: 1;
}

.dh-info {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0 0 20px;
  line-height: 1.5;
}

.dh-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dh-fields-row {
  display: flex;
  gap: 12px;
}

.dh-fields-row .dh-field {
  flex: 1;
}

.dh-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dh-field label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
}

.dh-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--border);
  border-radius: 12px;
  background: var(--bg-input);
  color: var(--text);
  font-size: 0.92rem;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.dh-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 10%, transparent);
  background: var(--bg-card);
}

.dh-btn {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.dh-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.dh-btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--primary) 28%, transparent);
}

.dh-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--primary-hover) 0%, color-mix(in srgb, var(--primary) 60%, #000) 100%);
  transform: translateY(-1px);
}

.dh-msg {
  font-size: 0.83rem;
  font-weight: 500;
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
}

.dh-msg--error {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

.dh-footer {
  background: var(--bg-input);
  border-top: 1px solid var(--border);
  padding: 10px 16px;
  text-align: center;
  font-size: 0.72rem;
  color: var(--text-muted);
}

.dh-footer strong {
  color: var(--primary);
  font-weight: 600;
}
</style>
